import React, { useState } from 'react';
import { Typography, Box, TextField, Button, Grid, Card, List, ListItem, ListItemText, InputAdornment } from '@mui/material';
import { Description, BarChart, Key, Security, Chat, HelpOutline, Book, Warning, Search, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { brand } from '../theme';
import { API_KEYS_URL, isExternal, linkProps } from '../links';

const searchTopics = [
  { title: 'Granular Reports', desc: 'Access detailed hazard/incident reports with /reports/data', to: '/docs#granular-reports' },
  { title: 'Aggregated Reports', desc: 'Get summarized report data with /reports/summary', to: '/docs#aggregated-reports' },
  { title: 'API Key Issuance', desc: 'Learn how to request an API key', to: API_KEYS_URL },
  { title: 'Rate Limiting', desc: 'Understand account rate limits and quotas', to: '/docs#rate-limits' },
  { title: 'Contact Us', desc: 'Reach out for support', to: '/contact' },
  { title: 'FAQs', desc: 'Common questions about our platforms', to: '/faq' },
  { title: 'API Documentation', desc: 'Detailed endpoint guides', to: '/docs' },
  { title: 'Report an Issue', desc: "Let us know if something's wrong", to: '/contact' },
];

function TopicCard({ icon, title, desc, to }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card
        {...linkProps(to)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 2.5,
          height: 168,
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            borderColor: '#3a4c63',
            bgcolor: brand.s2,
            transform: 'translateY(-3px)',
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            bgcolor: 'rgba(45,119,207,0.12)',
            color: brand.primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontSize: '1rem', color: brand.fg, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: brand.fg2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {desc}
        </Typography>
      </Card>
    </Grid>
  );
}

function Home() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  const suggestions = searchTopics
    .filter((topic) => searchTerm && topic.title.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 5);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
  };

  const openTopic = (to) => {
    if (isExternal(to)) window.open(to, '_blank', 'noopener,noreferrer');
    else navigate(to);
  };

  const handleSuggestionClick = (to) => {
    setSearchTerm('');
    setShowSuggestions(false);
    openTopic(to);
  };

  const handleSearch = () => {
    if (searchTerm.trim() === '') return;
    const matchedTopic = searchTopics.find((topic) => topic.title.toLowerCase().includes(searchTerm.toLowerCase()));
    setSearchTerm('');
    setShowSuggestions(false);
    openTopic(matchedTopic ? matchedTopic.to : '/docs');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          minHeight: 420,
          position: 'relative',
          borderRadius: '18px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          textAlign: 'center',
          border: `1px solid ${brand.border}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 60%), linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.3)), url("/appbar.webp")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(0.7) brightness(0.55)',
          }}
        />
        <Box sx={{ position: 'relative', maxWidth: 720 }}>
          <Typography
            variant="overline"
            sx={{ color: brand.primaryLight, display: 'block', mb: 2 }}
          >
            Arc Light Technologies
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, color: brand.fg, mb: 2 }}>
            API Documentation &amp;{' '}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(100deg, ${brand.primaryLight} 10%, ${brand.accent} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Key Management
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ color: brand.fg2, maxWidth: 560, mx: 'auto' }}>
            Access real-time open-source intelligence data with our API. Explore granular reports, missing-persons data, and aggregated analytics to stay informed.
          </Typography>
        </Box>
      </Box>

      {/* Search Field */}
      <Box sx={{ my: 6, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 520, mx: 'auto', position: 'relative' }}>
          <TextField
            placeholder="Search the docs"
            fullWidth
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onFocus={() => setShowSuggestions(searchTerm.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: brand.fg2 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <Button variant="contained" onClick={handleSearch} sx={{ ml: 1 }}>
                  Search
                </Button>
              ),
              sx: { pr: 0.5 },
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <List
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                bgcolor: brand.s1,
                border: `1px solid ${brand.border}`,
                borderRadius: '14px',
                zIndex: 10,
                maxHeight: 260,
                overflowY: 'auto',
                mt: 1,
                textAlign: 'left',
              }}
            >
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleSuggestionClick(suggestion.to)}
                  sx={{ '&:hover': { bgcolor: brand.s2 } }}
                >
                  <ListItemText
                    primary={suggestion.title}
                    secondary={suggestion.desc}
                    primaryTypographyProps={{ fontWeight: 600, color: brand.fg }}
                    secondaryTypographyProps={{ color: brand.fg2 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Popular Topics */}
      <Typography variant="h5" sx={{ mb: 2.5, color: brand.fg }}>
        Popular Topics
      </Typography>
      <Grid container spacing={2} sx={{ mb: 5 }}>
        <TopicCard icon={<Description />} title="Granular Reports" desc="Access detailed hazard/incident reports with /reports/data" to="/docs#granular-reports" />
        <TopicCard icon={<BarChart />} title="Aggregated Reports" desc="Get summarized report data with /reports/summary" to="/docs#aggregated-reports" />
        <TopicCard icon={<Key />} title="API Key Issuance" desc="Learn how to request an API key" to={API_KEYS_URL} />
        <TopicCard icon={<Security />} title="Rate Limiting" desc="Understand account rate limits and quotas" to="/docs#rate-limits" />
      </Grid>

      {/* Need Help? */}
      <Typography variant="h5" sx={{ mb: 2.5, color: brand.fg }}>
        Need Help?
      </Typography>
      <Grid container spacing={2}>
        <TopicCard icon={<Chat />} title="Contact Us" desc="Reach out for support" to="/contact" />
        <TopicCard icon={<HelpOutline />} title="FAQs" desc="Common questions about our platforms" to="/faq" />
        <TopicCard icon={<Book />} title="API Documentation" desc="Detailed endpoint guides" to="/docs" />
        <TopicCard icon={<Warning />} title="Report an Issue" desc="Let us know if something's wrong" to="/contact" />
      </Grid>

      {/* Bottom CTA */}
      <Box
        sx={{
          mt: 6,
          p: { xs: 3, md: 5 },
          borderRadius: '18px',
          border: `1px solid ${brand.border}`,
          bgcolor: brand.s1,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, color: brand.fg, mb: 1.5 }}>
          Ready to integrate?
        </Typography>
        <Typography variant="body1" sx={{ color: brand.fg2, mb: 3 }}>
          Request an API key and start pulling real-time OSINT data into your workflow.
        </Typography>
        <Button {...linkProps(API_KEYS_URL)} variant="contained" size="large" endIcon={<ArrowForward />}>
          Get Your API Key
        </Button>
      </Box>
    </Box>
  );
}

export default Home;
